import { retryLoad } from './retryLoad';
import { formFetch } from './formPreflight';
import { API_BASE } from './backendUrls';
import { sessionEndpoints, setSessionAccountType } from './portalSession';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sessionFetch, invalidateSessionRequests, confirmedInvalidResponse, sessionUnavailable, isInvalidSession, SESSION_INVALID_EVENT, SESSION_UNAVAILABLE_EVENT } from './sessionTransport';
import SessionRecovery from './SessionRecovery';

export const AuthContext = createContext();

const toArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === "string");
  }

  if (typeof value === "string" && value.trim()) {
    return [value];
  }

  if (Array.isArray(value?.data)) {
    return value.data.filter((item) => typeof item === "string");
  }

  // Supports an object such as: { DASHBOARD: true, CLIENTS: true }
  if (value && typeof value === "object") {
    return Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => key);
  }

  return [];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [checkingSession, setCheckingSession] = useState(false);
  const version = useRef(0);
  const pending = useRef(null);
  const userRef = useRef(null);
  const mounted = useRef(true);

  const applyAuthState = useCallback((payload = {}) => {
    userRef.current = payload.user || null;
    setSessionAccountType(payload.user?.type);
    setUser(payload.user || null);
    setPermissions(toArray(payload.permissions));
    setFeatures(toArray(payload.features));
    setTenant(payload.tenant || null);
    setAuthError(null);
  }, []);
  const setAuthState = useCallback((payload = {}) => {
    version.current++;
    invalidateSessionRequests();
    applyAuthState(payload);
    setLoading(false);
  }, [applyAuthState]);
  const clearAuthState = useCallback(() => {
    version.current++;
    invalidateSessionRequests();
    applyAuthState({});
    setLoading(false);
  }, [applyAuthState]);

  const checkAuth = useCallback(() => {
    if (pending.current) return pending.current;
    const currentVersion = version.current;
    setCheckingSession(true);
    pending.current = (async () => {
      try {
        const result = await retryLoad(async () => {
        const response = await sessionFetch(sessionEndpoints().me, { headers: { 'Content-Type': 'application/json' } });
        let result;
        try { result = await response.json(); } catch { throw sessionUnavailable(null, response.status); }
        if (!response.ok) {
          if (confirmedInvalidResponse(response, result)) {
            const error = new Error(result?.message || 'Session unavailable');
            error.code = 'SESSION_EXPIRED';
            throw error;
          }
          throw sessionUnavailable(null, response.status);
        }
        if (!result?.data?.user?.id) throw sessionUnavailable(null, response.status);
          return result;
        }, { timeoutMs: 12000, shouldRetry: error => ["SESSION_UNAVAILABLE", "LOAD_TIMEOUT"].includes(error.code) });
        if (mounted.current && version.current === currentVersion) applyAuthState(result.data);
        return result.data;
      } catch (error) {
        if (mounted.current && version.current === currentVersion) {
          if (isInvalidSession(error)) clearAuthState();
          else setAuthError(error.code === 'SESSION_UNAVAILABLE' ? error : sessionUnavailable(error));
        }
        return null;
      } finally {
        pending.current = null;
        if (mounted.current) {
          setCheckingSession(false);
          setLoading(false);
        }
      }
    })();
    return pending.current;
  }, [applyAuthState, clearAuthState]);

  useEffect(() => {
    mounted.current = true;
    const invalid = () => clearAuthState();
    const unavailable = event => {
      if (userRef.current) setAuthError(event.detail || sessionUnavailable());
    };
    window.addEventListener(SESSION_INVALID_EVENT, invalid);
    window.addEventListener(SESSION_UNAVAILABLE_EVENT, unavailable);
    checkAuth();
    return () => {
      mounted.current = false;
      window.removeEventListener(SESSION_INVALID_EVENT, invalid);
      window.removeEventListener(SESSION_UNAVAILABLE_EVENT, unavailable);
    };
  }, [checkAuth, clearAuthState]);

  const login = useCallback(async ({ endpoint = '/auth/login', email, password, ...extra }) => {
    const response = await sessionFetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, ...extra }),
    });
    let result;
    try { result = await response.json(); } catch { throw sessionUnavailable(null, response.status); }
    if (!response.ok) {
      if (response.status >= 500 || response.status === 429) throw sessionUnavailable(null, response.status);
      throw new Error(result?.message || 'Login failed');
    }
    if (!result?.data?.user?.id) throw sessionUnavailable(null, response.status);
    setAuthState(result.data);
    return result.data;
  }, [setAuthState]);

  const logout = useCallback(async () => {
    clearAuthState();
    try {
      await formFetch(API_BASE + '/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) { console.error(error); }
  }, [clearAuthState]);

  const hasPermission = useCallback((permission) => {
    const availablePermissions = toArray(permissions);

    return (
      Boolean(user?.isOwner) || availablePermissions.includes("ALL") ||
      availablePermissions.includes(permission)
    );
  }, [user, permissions]);

  const hasFeature = useCallback((feature) => {
    const availableFeatures = toArray(features);

    return (
      availableFeatures.includes("ALL") || availableFeatures.includes(feature)
    );
  }, [features]);

  const isAuthenticated = Boolean(user);

  const value = useMemo(
    () => ({
      user,
      tenant,
      permissions,
      features,
      loading,
      authError,
      checkingSession,
      isAuthenticated,
      login,
      logout,
      checkAuth,
      setAuthState,
      clearAuthState,
      hasPermission,
      hasFeature,
    }),
    [user, tenant, permissions, features, loading, authError, checkingSession, isAuthenticated, checkAuth, setAuthState, clearAuthState, login, logout, hasPermission, hasFeature],
  );

  return <AuthContext.Provider value={value}>{authError && user && <SessionRecovery compact />}{children}</AuthContext.Provider>;
};

